import * as React from 'react';
import { Database, Eye, GitBranch, Pencil, Users } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useTranslation } from '../../../hooks/useTranslation';

/**
 * The single thing a user has to learn about an Atlas canvas: which of three
 * modes it is in. One persistent chip, same three colours everywhere. Maps
 * 1:1 onto the artifact taxonomy (live view / planning diagram / snapshot).
 * See docs/plans/2026-08-03-feat-atlas-jobs-first-workspace-ux-plan.md.
 */
export type AtlasCanvasMode = 'live' | 'draft' | 'freeform' | 'historical';

interface AtlasModeChipProps {
  mode: AtlasCanvasMode;
  /** Live-view label specificity. */
  variant?: 'people' | 'position';
  folderId: string | null;
  /** Present only when the staffing workbench is available (position charts). */
  onOpenWorkbench?: () => void;
  /**
   * Pending draft changes overlaid on a live chart. While this is non-null the
   * chart is NOT a "read-only view of current data" — it is current data plus
   * a draft — and the chip must say so instead of contradicting the draft
   * banner in the panel.
   */
  draftChangeCount?: number | null;
  /**
   * Draft verbs (Apply / Awaiting approval / Discard), rendered inside the
   * pill while a draft is showing. The chip narrates the draft state, so the
   * actions live here too — a described draft whose buttons hide elsewhere is
   * how "staged, visible, unactionable" happened.
   */
  draftActions?: React.ReactNode;
}

const MODE_STYLE: Record<AtlasCanvasMode, string> = {
  historical: 'border-slate-200 bg-background/95 text-foreground',
  live: 'border-[color:var(--color-navy-200)] bg-background/95 text-foreground',
  draft: 'border-amber-200 bg-amber-50/95 text-amber-950',
  freeform: 'border-slate-200 bg-background/95 text-foreground',
};

export const AtlasModeChip: React.FC<AtlasModeChipProps> = ({
  mode,
  variant,
  folderId,
  onOpenWorkbench,
  draftChangeCount = null,
  draftActions = null,
}) => {
  const { t } = useTranslation('diagram');
  const liveWithDraft = mode === 'live' && draftChangeCount != null;
  const planningWithChanges = mode === 'draft'
    && draftChangeCount != null
    && draftChangeCount > 0;

  const { Icon, label, sub } = React.useMemo(() => {
    if (mode === 'historical') {
      return {
        Icon: Eye,
        label: t('atlasMode.historicalLabel', { defaultValue: 'Historical chart' }),
        sub: t('atlasMode.historicalSub', { defaultValue: '· read-only preview of past data' }),
      };
    }
    if (mode === 'draft') {
      return {
        Icon: GitBranch,
        label: planningWithChanges
          ? t('atlasMode.draftLabel', { defaultValue: 'Draft plan' })
          : t('atlasMode.planningLabel', { defaultValue: 'Planning chart' }),
        sub: draftActions
          ? t('atlasMode.planningUnappliedSub', { defaultValue: '· live organization unchanged' })
          : planningWithChanges
          ? t('atlasMode.draftPendingSub', {
              count: draftChangeCount,
              defaultValue: draftChangeCount === 1
                ? '· {{count}} pending change'
                : '· {{count}} pending changes',
            })
          : t('atlasMode.planningCleanSub', { defaultValue: '· no pending changes' }),
      };
    }
    if (mode === 'freeform') {
      return {
        Icon: Pencil,
        label: t('atlasMode.freeformLabel', { defaultValue: 'Freeform' }),
        sub: t('atlasMode.freeformSub', { defaultValue: '· your working copy, not linked to live data' }),
      };
    }
    const liveLabel = variant === 'position'
      ? t('atlasMode.livePositionLabel', { defaultValue: 'Live position chart' })
      : t('atlasMode.liveLabel', { defaultValue: 'Live org chart' });
    if (liveWithDraft) {
      // Short: the verbs beside it say the rest. "Nothing applied yet" is
      // implied by an Apply button that exists.
      return {
        Icon: GitBranch,
        label: liveLabel,
        sub: draftActions
          ? t('atlasMode.liveDraftUnappliedSub', { defaultValue: '· draft — live organization unchanged' })
          : draftChangeCount && draftChangeCount > 0
          ? t('atlasMode.liveDraftSub', {
              count: draftChangeCount,
              defaultValue: draftChangeCount === 1
                ? '· draft — {{count}} pending change'
                : '· draft — {{count}} pending changes',
            })
          : t('atlasMode.liveDraftEmptySub', { defaultValue: '· draft — no changes yet' }),
      };
    }
    return {
      Icon: Eye,
      label: liveLabel,
      sub: t('atlasMode.liveLinkedSub', { defaultValue: '· source data stays linked' }),
    };
  }, [draftActions, draftChangeCount, liveWithDraft, mode, planningWithChanges, t, variant]);

  return (
    <div
      data-testid={`tid.orgchart.modeChip.${liveWithDraft ? 'liveDraft' : mode}`}
      className={`absolute top-20 left-1/2 -translate-x-1/2 z-50 ${liveWithDraft ? 'w-[650px]' : 'w-auto'}`}
    >
      <div className={`flex items-center justify-center gap-3 rounded-full border py-1.5 pl-3 pr-1.5 shadow-lg backdrop-blur-sm ${liveWithDraft ? MODE_STYLE.draft : MODE_STYLE[mode]}`}>
        <div className="flex items-center gap-1.5 text-xs">
          <Icon className="size-3.5 shrink-0" />
          <span className="font-medium">{label}</span>
          <span className="hidden text-muted-foreground sm:inline">{sub}</span>
        </div>
        {(liveWithDraft || mode === 'draft') && draftActions}
        {onOpenWorkbench && mode !== 'historical' && !liveWithDraft && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-7 gap-1.5 rounded-full text-xs"
            data-testid="tid.orgchart.modeChip.workbench"
            onClick={onOpenWorkbench}
          >
            <Users className="size-3.5" />
            {t('atlasMode.openWorkbench', { defaultValue: 'Make changes' })}
          </Button>
        )}
        {mode === 'live' && folderId && !liveWithDraft && (
          <Button
            asChild
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 rounded-full text-xs text-muted-foreground"
            data-testid="tid.orgchart.modeChip.source"
          >
            <a href={`/dashboard/${encodeURIComponent(folderId)}/sources`}>
              <Database className="size-3.5" />
              {t('atlasMode.updateSource', { defaultValue: 'Update source' })}
            </a>
          </Button>
        )}
      </div>
    </div>
  );
};
